import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Megaphone, 
  Calendar, 
  AlertTriangle,
  Bell,
  ChevronLeft,
  ChevronRight,
  ExternalLink
} from 'lucide-react';

const STORAGE_KEY = 'killarney_dismissed_popups';
const CHECK_INTERVAL = 60000; // 1 minute

// Helper to safely read/write localStorage
const storage = {
  getDismissedIds: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : [];
    } catch {
      return [];
    }
  },
  setDismissedIds: (ids) => {
    try {
      const sanitized = Array.isArray(ids) ? ids.filter(id => typeof id === 'string') : [];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
    } catch {
      // Fail silently - popups will show again on next visit
    }
  }
};

// Popup type configuration
const POPUP_CONFIG = {
  urgent: { icon: AlertTriangle, badge: 'bg-red-500 text-white', border: 'border-red-500', header: 'bg-red-50', iconColor: 'text-red-500' },
  announcement: { icon: Megaphone, badge: 'bg-plp-blue text-white', border: 'border-plp-blue', header: 'bg-blue-50', iconColor: 'text-plp-blue' },
  event: { icon: Calendar, badge: 'bg-purple-500 text-white', border: 'border-purple-500', header: 'bg-purple-50', iconColor: 'text-purple-500' },
  custom: { icon: Bell, badge: 'bg-plp-gold text-plp-blue', border: 'border-plp-gold', header: 'bg-amber-50', iconColor: 'text-plp-gold' },
  default: { icon: Megaphone, badge: 'bg-slate-500 text-white', border: 'border-slate-500', header: 'bg-slate-50', iconColor: 'text-slate-500' }
};

const getPopupConfig = (type, priority) => {
  if (priority === 'urgent' || type === 'urgent') return POPUP_CONFIG.urgent;
  return POPUP_CONFIG[type] || POPUP_CONFIG.default;
};

// Sub-components for better organization
const PopupHeader = ({ popup, config, totalCount, currentIndex }) => {
  const IconComponent = config.icon;
  return (
    <div className={`${config.header} px-6 py-4`}>
      <DialogHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={config.iconColor}>
              <IconComponent className="w-5 h-5" />
            </div>
            <DialogTitle className="text-lg font-semibold">{popup.title}</DialogTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={config.badge}>
              {popup.priority === 'urgent' ? 'URGENT' : popup.type.toUpperCase()}
            </Badge>
            {totalCount > 1 && (
              <Badge variant="outline" className="bg-white">
                {currentIndex + 1}/{totalCount}
              </Badge>
            )}
          </div>
        </div>
      </DialogHeader>
    </div>
  );
};

const PopupContent = ({ popup }) => (
  <div className="px-6 py-4 space-y-4">
    {popup.image_url && (
      <div className="rounded-lg overflow-hidden border">
        <img 
          src={popup.image_url.startsWith('/api') 
            ? `${API.replace('/api', '')}${popup.image_url}` 
            : popup.image_url
          }
          alt={popup.title}
          className="w-full h-48 object-cover"
        />
      </div>
    )}
    
    <p className="text-slate-700 whitespace-pre-wrap">{popup.content}</p>

    {popup.link_url && (
      <a 
        href={popup.link_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-plp-blue hover:underline font-medium"
      >
        Learn More <ExternalLink className="w-4 h-4" />
      </a>
    )}

    {popup.event_date && (
      <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
        <Calendar className="w-4 h-4" />
        <span>
          {popup.event_date}
          {popup.event_time && ` at ${popup.event_time}`}
          {popup.event_location && ` - ${popup.event_location}`}
        </span>
      </div>
    )}
  </div>
);

const PopupFooter = ({ totalCount, currentIndex, setCurrentIndex, onDismiss, onDismissAll }) => (
  <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between">
    <div className="flex gap-2">
      {totalCount > 1 && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            data-testid="popup-prev-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentIndex(prev => Math.min(totalCount - 1, prev + 1))}
            disabled={currentIndex === totalCount - 1}
            data-testid="popup-next-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </>
      )}
    </div>
    
    <div className="flex gap-2">
      {totalCount > 1 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismissAll}
          className="text-slate-500"
          data-testid="popup-dismiss-all-btn"
        >
          Dismiss All
        </Button>
      )}
      <Button
        onClick={onDismiss}
        className="bg-plp-blue hover:bg-plp-blue-light"
        data-testid="popup-dismiss-btn"
      >
        Got it
      </Button>
    </div>
  </div>
);

const GlobalPopupModal = () => {
  const [popups, setPopups] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(storage.getDismissedIds);
  const isFirstCheck = useRef(true);
  const popupsRef = useRef([]);

  // Keep ref in sync for use in fetchPopups
  useEffect(() => {
    popupsRef.current = popups;
  }, [popups]);

  const fetchPopups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/global-popups/active`);
      const activePopups = response.data.filter(
        popup => !dismissedIds.includes(popup.id)
      );
      
      if (isFirstCheck.current) {
        isFirstCheck.current = false;
        if (activePopups.length > 0) {
          setPopups(activePopups);
          setIsOpen(true);
        }
        return;
      }
      
      // Show new popups only
      if (activePopups.length > popupsRef.current.length) {
        const hasNewPopups = activePopups.some(
          p => !popupsRef.current.some(existing => existing.id === p.id)
        );
        if (hasNewPopups) {
          setPopups(activePopups);
          setCurrentIndex(0);
          setIsOpen(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch global popups:', error);
    }
  }, [dismissedIds]);

  useEffect(() => {
    fetchPopups();
    const interval = setInterval(fetchPopups, CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchPopups]);

  const handleDismiss = useCallback((popupId) => {
    const newDismissed = [...dismissedIds, popupId];
    setDismissedIds(newDismissed);
    storage.setDismissedIds(newDismissed);
    
    const remainingPopups = popups.filter(p => p.id !== popupId);
    if (remainingPopups.length === 0) {
      setIsOpen(false);
      setPopups([]);
    } else {
      setPopups(remainingPopups);
      setCurrentIndex(prev => Math.min(prev, remainingPopups.length - 1));
    }
  }, [dismissedIds, popups]);

  const handleDismissAll = useCallback(() => {
    const allIds = [...dismissedIds, ...popups.map(p => p.id)];
    setDismissedIds(allIds);
    storage.setDismissedIds(allIds);
    setPopups([]);
    setIsOpen(false);
  }, [dismissedIds, popups]);

  const currentPopup = popups[currentIndex];
  if (!currentPopup) return null;

  const config = getPopupConfig(currentPopup.type, currentPopup.priority);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent 
        className={`sm:max-w-lg border-t-4 ${config.border} p-0 overflow-hidden`} 
        data-testid="global-popup-modal"
      >
        <PopupHeader 
          popup={currentPopup} 
          config={config} 
          totalCount={popups.length} 
          currentIndex={currentIndex} 
        />
        <PopupContent popup={currentPopup} />
        <PopupFooter 
          totalCount={popups.length}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          onDismiss={() => handleDismiss(currentPopup.id)}
          onDismissAll={handleDismissAll}
        />
      </DialogContent>
    </Dialog>
  );
};

export default GlobalPopupModal;
